import React from 'react';
import { Home, ClipboardList, User, Briefcase, Wrench, LayoutDashboard, Store, Users, LogOut, DollarSign, Languages, Banknote, Tag, Bell, RefreshCw, BookOpen } from 'lucide-react';
import { ViewState, UserRole, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface NavigationProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  userRole: UserRole;
  onLogout: () => void;
  isSuperAdmin?: boolean;
  onSwitchRole?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ currentView, onNavigate, userRole, onLogout, isSuperAdmin, onSwitchRole }) => {
  const { language, setLanguage } = useTranslation();
  if (currentView === 'AUTH') return null;

  const isShop = userRole === UserRole.SHOP_OWNER || userRole === UserRole.STAFF;
  const isOwner = userRole === UserRole.SHOP_OWNER;
  const isAdmin = userRole === UserRole.ADMIN;
  const isCustomer = userRole === UserRole.CUSTOMER;

  const labels: Record<string, Record<Language, string>> = {
    marketplace: { EN: 'Marketplace', KH: 'ទីផ្សារ', ZH: '市场', JA: 'マーケット', KO: '마켓플레이스' },
    workspace: { EN: 'Workspace', KH: 'កន្លែងធ្វើការ', ZH: '工作区', JA: 'ワークスペース', KO: '워크스페이스' },
    account: { EN: 'Account', KH: 'គណនី', ZH: '账户', JA: 'アカウント', KO: '계정' },
    explore: { EN: 'Explore', KH: 'ស្វែងរក', ZH: '探索', JA: '探す', KO: '탐색' },
    my_repairs: { EN: 'My Repairs', KH: 'ការជួសជុលរបស់ខ្ញុំ', ZH: '我的维修', JA: '修理履歴', KO: '내 수리' },
    reminders: { EN: 'Reminders', KH: 'ការរំលឹក', ZH: '提醒', JA: 'リマインダー', KO: '알림' },
    dashboard: { EN: 'Dashboard', KH: 'ផ្ទាំងគ្រប់គ្រង', ZH: '仪表板', JA: 'ダッシュボード', KO: '대시보드' },
    services: { EN: 'Services', KH: 'សេវាកម្ម', ZH: '服务', JA: 'サービス', KO: '服务' },
    finance: { EN: 'Finance', KH: 'ហិរញ្ញវត្ថុ', ZH: '财务', JA: '財務', KO: '재무' },
    my_shop: { EN: 'My Shop', KH: 'ហាងរបស់ខ្ញុំ', ZH: '我的店铺', JA: 'マイショップ', KO: '내 상점' },
    overview: { EN: 'Overview', KH: 'ទិដ្ឋភាពទូទៅ', ZH: '概览', JA: '概要', KO: '개요' },
    shops: { EN: 'Shops', KH: 'បញ្ជីហាង', ZH: '店铺', JA: '店舗', KO: '상점' },
    users: { EN: 'Users', KH: 'អ្នកប្រើប្រាស់', ZH: '用户', JA: 'ユーザー', KO: '사용자' },
    profile: { EN: 'Profile', KH: 'ព័ត៌មានផ្ទាល់ខ្លួន', ZH: '资料', JA: 'プロフィール', KO: '프로필' },
    manual: { EN: 'Manual', KH: 'សៀវភៅណែនាំ', ZH: '手册', JA: 'マニュアル', KO: '매뉴얼' },
    switch_role: { EN: 'Switch Role', KH: 'ប្តូរតួនាទីសាកល្បង', ZH: '切换角色', JA: 'ロール切替', KO: '역할 전환' },
    sign_out: { EN: 'Sign Out', KH: 'ចាកចេញ', ZH: '退出', JA: 'サインアウト', KO: '로그아웃' },
  };

  const navItems = [
    { section: labels.marketplace[language], items: [
      { view: 'HOME', label: labels.explore[language], icon: Home, show: isCustomer },
      { view: 'BOOKINGS', label: labels.my_repairs[language], icon: ClipboardList, show: isCustomer },
      { view: 'REMINDERS', label: labels.reminders[language], icon: Bell, show: isCustomer },
    ]},
    { section: labels.workspace[language], items: [
      { view: 'SHOP_DASHBOARD', label: labels.dashboard[language], icon: Briefcase, show: isShop },
      { view: 'SHOP_SERVICES', label: labels.services[language], icon: Tag, show: isShop },
      { view: 'SHOP_FINANCE', label: labels.finance[language], icon: Banknote, show: isOwner },
      { view: 'SHOP_PROFILE', label: labels.my_shop[language], icon: Store, show: isShop },
      { view: 'ADMIN_DASHBOARD', label: labels.overview[language], icon: LayoutDashboard, show: isAdmin },
      { view: 'ADMIN_SHOPS', label: labels.shops[language], icon: Store, show: isAdmin },
      { view: 'ADMIN_USERS', label: labels.users[language], icon: Users, show: isAdmin },
    ]},
    { section: labels.account[language], items: [
      { view: 'PROFILE', label: labels.profile[language], icon: User, show: true },
      { view: 'MANUAL', label: labels.manual[language], icon: BookOpen, show: true },
    ]}
  ];

  const getActiveStyle = (view: string) => 
    currentView === view 
      ? 'bg-violet-600 text-white shadow-lg shadow-violet-200' 
      : 'text-slate-500 hover:bg-violet-50 hover:text-violet-600';

  const roleLabels: Record<UserRole, Record<Language, string>> = {
    [UserRole.CUSTOMER]: { EN: 'Customer', KH: 'អតិថិជន', ZH: '客户', JA: '顧客', KO: '고객' },
    [UserRole.SHOP_OWNER]: { EN: 'Shop Owner', KH: 'ម្ចាស់ហាង', ZH: '店主', JA: '店主', KO: '상점 주인' },
    [UserRole.STAFF]: { EN: 'Technician', KH: 'បុគ្គលិកបច្ចេកទេស', ZH: '技师', JA: '技術者', KO: '기술자' },
    [UserRole.ADMIN]: { EN: 'Admin', KH: 'អ្នកគ្រប់គ្រងប្រព័ន្ធ', ZH: '管理员', JA: '管理者', KO: '관리자' }
  };

  const languages: { code: Language; label: string }[] = [
    { code: 'KH', label: 'KH' },
    { code: 'EN', label: 'EN' },
    { code: 'ZH', label: 'ZH' },
    { code: 'JA', label: 'JA' },
    { code: 'KO', label: 'KO' },
  ];

  const bottomNavItems = (isAdmin) 
    ? [
        { view: 'ADMIN_DASHBOARD', label: labels.overview[language], icon: LayoutDashboard },
        { view: 'ADMIN_SHOPS', label: labels.shops[language], icon: Store },
        { view: 'ADMIN_USERS', label: labels.users[language], icon: Users },
        { view: 'PROFILE', label: labels.profile[language], icon: User }
      ]
    : (isShop)
    ? [
        { view: 'SHOP_DASHBOARD', label: labels.dashboard[language], icon: Briefcase },
        { view: 'SHOP_SERVICES', label: labels.services[language], icon: Tag },
        { view: 'SHOP_PROFILE', label: labels.my_shop[language], icon: Store },
        { view: 'PROFILE', label: labels.profile[language], icon: User }
      ]
    : [
        { view: 'HOME', label: labels.explore[language], icon: Home },
        { view: 'BOOKINGS', label: labels.my_repairs[language], icon: ClipboardList },
        { view: 'REMINDERS', label: labels.reminders[language], icon: Bell },
        { view: 'PROFILE', label: labels.profile[language], icon: User }
      ];

  return (
    <>
      <div className="md:hidden fixed top-0 left-0 w-full p-6 z-40 bg-gradient-to-b from-slate-50/95 to-transparent pointer-events-none flex justify-between items-center">
        <div className="flex items-center space-x-2 pointer-events-auto">
            <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-violet-200">
               <Wrench size={16} />
            </div>
            <span className="text-lg font-black tracking-widest uppercase text-slate-900 drop-shadow-sm">CHEANG</span>
        </div>
        <div className="flex gap-2 pointer-events-auto">
          {isSuperAdmin && (
            <button onClick={onSwitchRole} className="bg-white/90 backdrop-blur-md p-2 rounded-xl border border-violet-100 text-violet-600 shadow-lg">
              <RefreshCw size={16} />
            </button>
          )}
          <div className="bg-white/90 backdrop-blur-md p-1 rounded-2xl border border-slate-100 flex gap-1 shadow-lg overflow-x-auto max-w-[150px] no-scrollbar">
             {languages.map(l => (
               <button key={l.code} onClick={() => setLanguage(l.code)} className={`px-2 py-1 rounded-lg text-[10px] font-black transition-all ${language === l.code ? 'bg-slate-900 text-white' : 'text-slate-400'}`}>
                 {l.code}
               </button>
             ))}
          </div>
        </div>
      </div>

      <div className="hidden md:flex flex-col fixed top-0 left-0 bottom-0 w-72 bg-white border-r border-slate-100 z-50">
        <div className="p-8">
          <div className="flex items-center space-x-3 text-violet-600 mb-2">
            <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-violet-200">
               <Wrench size={16} />
            </div>
            <span className="text-xl font-black tracking-widest uppercase text-slate-900">CHEANG</span>
          </div>
          <div className="px-1 flex justify-between items-center">
             <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">
               {roleLabels[userRole][language]}
             </span>
             <div className="bg-slate-50 p-1 rounded-lg flex gap-1 border border-slate-100 overflow-x-auto no-scrollbar max-w-[120px]">
                {languages.map(l => (
                  <button key={l.code} onClick={() => setLanguage(l.code)} className={`px-1.5 py-0.5 rounded-md text-[9px] font-black transition-all ${language === l.code ? 'bg-white shadow-sm text-slate-900' : 'text-slate-300'}`}>
                    {l.code}
                  </button>
                ))}
             </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-6 space-y-8">
          {navItems.map((group, idx) => {
            const visibleItems = group.items.filter(i => i.show);
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={idx}>
                <h3 className="px-4 text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  {group.section}
                </h3>
                <div className="space-y-1">
                  {visibleItems.map(item => (
                    <button
                      key={item.label}
                      onClick={() => onNavigate(item.view as ViewState)}
                      className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all duration-200 ${getActiveStyle(item.view)}`}
                    >
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          
          {isSuperAdmin && (
            <div className="px-4 pt-4 border-t border-slate-50">
               <button 
                onClick={onSwitchRole}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-sm font-black text-violet-600 hover:bg-violet-50 transition-all uppercase tracking-widest"
               >
                 <RefreshCw size={20} />
                 <span>{labels.switch_role[language]}</span>
               </button>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-50">
          <button 
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-sm font-semibold text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut size={20} />
            <span>{labels.sign_out[language]}</span>
          </button>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 w-full z-50 bg-white/80 backdrop-blur-xl border-t border-slate-100 flex justify-around items-center h-20 px-4 pb-2 shadow-[0_-8px_30px_rgb(0,0,0,0.04)]">
         {bottomNavItems.map((item) => {
           const isActive = currentView === item.view;
           return (
             <button
               key={item.view}
               onClick={() => onNavigate(item.view as ViewState)}
               className={`flex flex-col items-center justify-center gap-1.5 px-2 transition-all duration-300 relative ${isActive ? 'text-violet-600 scale-110' : 'text-slate-400 hover:text-slate-600'}`}
             >
               {isActive && (
                 <div className="absolute -top-3 w-1.5 h-1.5 bg-violet-600 rounded-full shadow-[0_0_10px_rgba(124,58,237,0.5)] animate-pulse" />
               )}
               <item.icon size={isActive ? 24 : 20} strokeWidth={isActive ? 2.5 : 2} />
               <span className={`text-[9px] font-black uppercase tracking-tighter transition-all ${isActive ? 'opacity-100' : 'opacity-60'}`}>
                 {item.label}
               </span>
             </button>
           );
         })}
      </div>
    </>
  );
};
